import React, { useCallback, useMemo, useEffect, useState, useRef } from 'react';
import { Grid, Icon, Loader, Message } from 'semantic-ui-react';
import {
  useVariationZone,
  useCharacterZone,
  useSharedData
} from '../../hooks/core/useZoneHooks';
import '../../styles/voiceActorWorksheet.css';

// Custom comparison function for VariationRow
const arePropsEqual = (prevProps, nextProps) => {
  // Only re-render if these specific props change
  return (
    prevProps.variation.uuid === nextProps.variation.uuid &&
    prevProps.variation.name === nextProps.variation.name &&
    prevProps.variation.version === nextProps.variation.version &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.characterId === nextProps.characterId &&
    prevProps.isDisabled === nextProps.isDisabled &&
    prevProps.versionCount === nextProps.versionCount
  );
};

// Simplify the VariationRow component
const VariationRow = React.memo(({
  variation,
  versionCount,
  characterId,
  isSelected,
  onSelect,
  onDelete
}) => {
  return (
    <Grid.Row
      className={`va-variation-list-item ${isSelected ? 'selected' : ''}`}
      onClick={() => onSelect(variation)}
      style={{ cursor: 'pointer' }}
    >
      <Grid.Column width={8}>{variation.name}</Grid.Column>
      <Grid.Column textAlign='center' width={3}>{versionCount}</Grid.Column>
      <Grid.Column width={5} textAlign="right">
        <Icon
          name="trash alternate"
          color="red"
          className="va-trash-icon"
          style={{ cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            onDelete(variation);
          }}
        />
      </Grid.Column>
    </Grid.Row>
  );
}, arePropsEqual);

/**
 * Enhanced Variation List component for the VoiceActor Worksheet module
 * 
 * @param {Object} props - Component props
 * @param {Function} props.onVariationSelect - Function to handle variation selection
 * @param {Function} props.onDeleteVariation - Function to handle variation deletion
 * @param {Object} props.onVariationListRefreshRef - Ref for parent component to trigger refresh
 * @returns {JSX.Element} VariationsListEnh component
 */
const VariationsListEnh = ({
  onVariationSelect,
  onDeleteVariation,
  onVariationListRefreshRef
}) => {
  // Use zone-based hooks
  const variationZone = useVariationZone();
  const characterZone = useCharacterZone();
  const sharedData = useSharedData();

  const {
    variations,
    activeVariation,
    variationCounts,
    loading,
    actions: variationActions
  } = variationZone;

  const { selectedCharacter } = characterZone;
  const { selections } = sharedData;

  // Extract derived values
  const characterId = selections.characterId || selectedCharacter?.uuid;
  const portfolioId = selections.portfolioId;
  const variationId = activeVariation?.uuid;

  // Filter variations for the current character - moved to useMemo to fix dependency warning
  const characterVariations = useMemo(() => {
    return characterId && variations ?
      variations.filter(variation => variation.characterId === characterId) : [];
  }, [characterId, variations]);

  // Add render tracking
  const renderCount = useRef(0);
  renderCount.current++;

  // Local state for UI-specific concerns
  const [localSelectedId, setLocalSelectedId] = useState(variationId);
  const [errorMessage, setErrorMessage] = useState('');

  // Function to force refresh the variations list
  const refreshVariations = useCallback(() => {
    console.log('[VA_VARIATION_LIST] refreshVariations called - REFRESH TRIGGERED');

    // Refresh both variations and variation counts when called
    if (characterId && portfolioId) {
      console.log('[VA_VARIATION_LIST] Refreshing variations and counts for character', characterId);
      variationActions.fetchVariations(characterId, portfolioId);
      variationActions.fetchVariationCounts(characterId);
    }
  }, [characterId, portfolioId, variationActions]);

  // Expose the refresh function to the parent component via ref
  useEffect(() => {
    if (onVariationListRefreshRef && typeof onVariationListRefreshRef === 'object' && 'current' in onVariationListRefreshRef) {
      onVariationListRefreshRef.current = refreshVariations;
    }
  }, [refreshVariations, onVariationListRefreshRef]);

  // Update local selected ID when prop changes
  useEffect(() => {
    if (variationId !== localSelectedId) {
      setLocalSelectedId(variationId);
    }
  }, [variationId, localSelectedId]);

  useEffect(() => {
    if (characterId) {
      console.log('[VA_VARIATION_LIST] Fetching variations for character:', characterId);
      if (portfolioId) {
        variationActions.fetchVariations(characterId, portfolioId);
        variationActions.fetchVariationCounts(characterId);
      }
    }
  }, [characterId, portfolioId, variationActions]);

  // Debug log to see what data we're getting
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[VA_VARIATION_LIST] Debug - variations:', variations);
      console.log('[VA_VARIATION_LIST] Debug - variationCounts:', variationCounts);
      console.log('[VA_VARIATION_LIST] Debug - characterVariations:', characterVariations);
    }
  }, [variations, variationCounts, characterVariations]);

  // Reset component when characterId changes
  useEffect(() => {
    // Clear local state when character changes
    setErrorMessage('');
  }, [characterId]);

  // Enhanced handler for variation selection with error handling
  const handleSelect = useCallback((variation) => {
    // If this variation is already selected, don't do anything
    if (localSelectedId === variation.uuid) {
      return;
    }

    // Clear any previous errors
    setErrorMessage('');

    // Update local selection state immediately for responsive UI
    setLocalSelectedId(variation.uuid);

    // Call the parent's onVariationSelect function
    try {
      onVariationSelect(variation);
    } catch (error) {
      console.error('[VA_VARIATION_LIST] Error selecting variation:', error);
      setErrorMessage(`Error loading variation: ${error.message || 'Unknown error'}`);
    }
  }, [
    onVariationSelect,
    localSelectedId
  ]);

  // Helper function to get version count for a variation
  const getVersionCount = useCallback((variation) => {
    // If this is the currently active variation and we have samples loaded, use the actual samples count
    if (activeVariation?.uuid === variation.uuid && variationZone.samples && variationZone.samples.length > 0) {
      return variationZone.samples.length;
    }

    // Check if the variation has a speechSamples property (from API response)
    if (variation.speechSamples && Array.isArray(variation.speechSamples)) {
      return variation.speechSamples.length;
    }

    // Check if the variation has a samples property
    if (variation.samples && Array.isArray(variation.samples)) {
      return variation.samples.length;
    }

    // Check if the variation has a versionCount property
    if (typeof variation.versionCount === 'number') {
      return variation.versionCount;
    }

    // Check if the variation has a samplesCount property
    if (typeof variation.samplesCount === 'number') {
      return variation.samplesCount;
    }

    // Fall back to the variationCounts from the API (now fixed to work correctly)
    const apiCount = variationCounts[variation.uuid];
    if (typeof apiCount === 'number' && apiCount > 0) {
      return apiCount;
    }

    // FIXED: For VoiceActor, default to 1 since each variation represents at least one version
    // (the variation itself), even if no separate samples exist in the database yet
    return 1;
  }, [activeVariation, variationZone.samples, variationCounts]);

  // Memoize the sorted variations array
  const sortedVariations = useMemo(() => {
    if (!Array.isArray(characterVariations)) return [];

    console.log(`[VA_VARIATION_LIST] Variations for character ${characterId}:`, characterVariations);

    // Check for duplicates
    const uuids = new Set();
    const uniqueVars = [];
    characterVariations.forEach(v => {
      if (v && v.uuid && !uuids.has(v.uuid)) {
        uuids.add(v.uuid);
        uniqueVars.push(v);
      } else {
        console.warn(`[VA_VARIATION_LIST] Duplicate variation found with UUID ${v?.uuid}`);
      }
    });

    console.log(`[VA_VARIATION_LIST] Original variations count: ${characterVariations.length}, Unique variations count: ${uniqueVars.length}`);

    return uniqueVars.sort((a, b) => a.name.localeCompare(b.name));
  }, [characterVariations, characterId]);

  // Clear error message after a timeout
  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage('');
      }, 5000); // Clear after 5 seconds

      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  // Show loading state if fetching
  const dataLoading = loading && sortedVariations.length === 0;

  return (
    <div style={{ position: 'relative' }}>
      {/* Error message display */}
      {errorMessage && (
        <Message
          negative
          style={{ marginTop: '10px', marginBottom: '10px' }}
        >
          <Message.Header>Error</Message.Header>
          <p>{errorMessage}</p>
        </Message>
      )}

      <div style={{ marginTop: '10px' }}>
        {/* Header row */}
        <div className="va-variation-list-container">
          <Grid className='listGrid'>
            <Grid.Row style={{ fontWeight: 'bold', borderBottom: '1px solid #ddd', padding: "8px 8px 8px 10px" }}>
              <Grid.Column width={8}>Name</Grid.Column>
              <Grid.Column width={3}>Versions</Grid.Column>
              <Grid.Column width={5}></Grid.Column>
            </Grid.Row>

            {/* Loading indicator */}
            {dataLoading ? (
              <Grid.Row style={{ padding: '20px 0', textAlign: 'center' }}>
                <Grid.Column width={16}>
                  <Loader active inline="centered" size="small">Loading Variations</Loader>
                </Grid.Column>
              </Grid.Row>
            ) : !Array.isArray(sortedVariations) || sortedVariations.length === 0 ? (
              <Grid.Row style={{ padding: '20px 0', textAlign: 'center' }}>
                <Grid.Column width={16}>
                  <p style={{ color: '#888' }}>No variations found. Create a new variation to get started.</p>
                </Grid.Column>
              </Grid.Row>
            ) : (
              sortedVariations.map((variation, index) => (
                <VariationRow
                  key={`${variation.uuid}-${index}`}
                  variation={variation}
                  characterId={characterId}
                  isSelected={localSelectedId === variation.uuid}
                  onSelect={handleSelect}
                  onDelete={onDeleteVariation}
                  versionCount={getVersionCount(variation)}
                />

              ))
            )}
          </Grid>
        </div>
      </div>
    </div>
  );
};

// Add display name for debugging
VariationsListEnh.displayName = 'VariationsListEnh';

// Memoize component to prevent unnecessary re-renders during text editing
const areVariationListPropsEqual = (prevProps, nextProps) => {
  // Compare function props by reference (should be stable with useCallback)
  return (
    prevProps.onVariationSelect === nextProps.onVariationSelect &&
    prevProps.onDeleteVariation === nextProps.onDeleteVariation &&
    prevProps.onVariationListRefreshRef === nextProps.onVariationListRefreshRef
  );
};

export default React.memo(VariationsListEnh, areVariationListPropsEqual);
